import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, FileText, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNotifications } from "@/hooks/useNotifications";

const formatRelative = (iso: string) => {
  const d = new Date(iso).getTime();
  const diff = Date.now() - d;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(iso).toLocaleDateString("en-ZA", { month: "short", day: "numeric" });
};

export const NotificationBell = () => {
  const { notifications, unreadCount, markAllRead, markRead, clearAll } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleToggle = () => {
    setOpen((v) => {
      const next = !v;
      if (next && unreadCount > 0) {
        // Defer so the badge doesn't disappear before the panel opens.
        setTimeout(markAllRead, 400);
      }
      return next;
    });
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={handleToggle}
        aria-label={`Notifications${unreadCount ? ` (${unreadCount} unread)` : ""}`}
        className="relative grid h-8 w-8 place-items-center rounded-full border border-hairline bg-card text-ink-mid transition-colors hover:bg-surface hover:text-foreground"
      >
        <Bell className="h-4 w-4" strokeWidth={1.6} />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-brass-dark px-1 text-[0.6rem] font-bold leading-none text-bone">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-[340px] overflow-hidden rounded-xl border border-hairline bg-background shadow-card-hover">
          <div className="flex items-center justify-between border-b border-hairline px-4 py-3">
            <div>
              <div className="text-[0.85rem] font-semibold text-foreground">Notifications</div>
              <div className="text-[0.7rem] text-muted-foreground">
                {notifications.length === 0
                  ? "You're all caught up"
                  : `${notifications.length} recent`}
              </div>
            </div>
            {notifications.length > 0 && (
              <button
                onClick={clearAll}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[0.72rem] font-medium text-ink-mid hover:bg-surface hover:text-foreground"
              >
                <Trash2 className="h-3 w-3" strokeWidth={1.8} />
                Clear
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <div className="mx-auto mb-2 grid h-10 w-10 place-items-center rounded-full border border-hairline bg-card">
                <Bell className="h-4 w-4 text-muted-foreground" strokeWidth={1.6} />
              </div>
              <p className="text-[0.8rem] text-muted-foreground">
                When your drafts are ready, they'll show up here.
              </p>
            </div>
          ) : (
            <ul className="max-h-[420px] divide-y divide-hairline overflow-y-auto">
              {notifications.map((n) => {
                const Inner = (
                  <div className="flex items-start gap-3 px-4 py-3 transition-colors hover:bg-surface">
                    <div
                      className={cn(
                        "mt-0.5 grid h-8 w-8 flex-shrink-0 place-items-center rounded-[8px] border",
                        n.read ? "border-hairline bg-card" : "border-foreground/40 bg-surface",
                      )}
                    >
                      <FileText className="h-3.5 w-3.5 text-foreground" strokeWidth={1.6} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="text-[0.8rem] font-medium text-foreground">{n.title}</div>
                        {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-yellow-dark" />}
                      </div>
                      <div className="mt-0.5 truncate text-[0.75rem] text-ink-mid">{n.body}</div>
                      <div className="mt-1 text-[0.68rem] text-muted-foreground">
                        {formatRelative(n.createdAt)}
                      </div>
                    </div>
                  </div>
                );
                return (
                  <li key={n.id}>
                    {n.href ? (
                      <Link to={n.href} onClick={() => { markRead(n.id); setOpen(false); }} className="block">
                        {Inner}
                      </Link>
                    ) : (
                      <button onClick={() => markRead(n.id)} className="block w-full text-left">
                        {Inner}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
